import React, {useEffect} from 'react';
import { useTranslation } from "react-i18next";
import { useAssetAssignment } from '../providers/AssetAssignmentProvider';
import { Status } from "../../../../../models/enums";
import "./BulkAssetAssignment.scss"
import useAssetCustomFields from "../../../../../hooks/useAssetCustomFields";
import AssetCustomField from "../../../../../models/AssetCustomField";

interface CustomField {
    customFieldId: number;
    customFieldName: string;
    value: any;
}

interface CustomFieldValues {
    assetId: number;
    countItemId: string;
    id: string;
    itemId: string;
    customFields: CustomField[];
}

interface AssetCustomFieldsComponentProps {
    parentId: string;
    assetId?: number | undefined;
    assetTypeId: number | undefined;
    siteId?: number | undefined;
    itemId?: string | undefined;
    setAssetCustomFields?: (customFields: any[]) => void;
    assetCustomFields: any[];
    loadStatus?: Status;
    onFetchedValuesChange?: (values: any[]) => void;
}

const AssetCustomFieldsComponent: React.FC<AssetCustomFieldsComponentProps> = (
    {parentId,
        assetId,
        assetTypeId,
        siteId,
        itemId,
        assetCustomFields,
        loadStatus,
        onFetchedValuesChange, }
) => {
    const {t} = useTranslation();
    const {customFieldValues, setCustomFieldValues} = useAssetAssignment();
    const { getCustomFieldValuesByCountItem } = useAssetCustomFields(assetTypeId, siteId);

    const customFieldsLimit = 4;

    useEffect(() => {
        const fetchAndMatchCustomFields = async () => {
            if (assetId && itemId) {
                // @ts-ignore
                const existingCustomFieldValues: CustomFieldValues[] = await getCustomFieldValuesByCountItem(itemId, assetId);
                if (onFetchedValuesChange) {
                    onFetchedValuesChange(existingCustomFieldValues);
                }
                if (existingCustomFieldValues && existingCustomFieldValues.length > 0) {
                    const updatedFieldValues: any[] = [];
                    existingCustomFieldValues.forEach((customFieldValue) => {
                        customFieldValue.customFields.forEach((customField) => {
                            assetCustomFields.forEach((assetCustomField, index) => {
                                if (assetCustomField.id === customField.customFieldId) {
                                    updatedFieldValues[index] = customField.value;
                                }
                            });
                        });
                    });
                    setCustomFieldValues({
                        ...customFieldValues,
                        ...{
                            [parentId]: updatedFieldValues
                        }
                    });
                }
            }
        };

        fetchAndMatchCustomFields();
    }, []);

    const handleInputChange = (index: number, value: any) => {
        const fieldType = assetCustomFields[index].type;
        let updatedValue: any = value;

        if (value === "") {
            updatedValue = null;
        } else if (fieldType === "Numeric") {
            updatedValue = parseFloat(value);
        }

        setCustomFieldValues({
            ...customFieldValues,
            ...(itemId === undefined
                ? {
                    [parentId]: {
                        ...customFieldValues[parentId],
                        [index]: updatedValue,
                    },
                }
                : {
                    [itemId]: {
                        ...(customFieldValues[parentId] || {}),
                        ...customFieldValues[itemId],
                        [index]: updatedValue,
                    },
                }),
        });
    };

    const fieldValues = itemId && customFieldValues?.[itemId] !== undefined
        ? customFieldValues?.[itemId]
        : customFieldValues?.[parentId] ?? undefined;

    if (loadStatus === Status.LOADING) return <div>{t("projects.summary.itemsTable.loadAssetCustomFields.loading")}</div>;
    if (loadStatus === Status.ERROR) return <div>{t("projects.summary.itemsTable.loadAssetCustomFields.loadErrorDesc")}</div>;
    if (loadStatus === Status.NO_DATA) return <div>{t("projects.summary.itemsTable.loadAssetCustomFields.noDataDesc")}</div>;

    return (
        <div className='asset-custom-fields_box'>
            <ul className='asset-custom-fields_list'
                style={{ height: assetCustomFields.length >= customFieldsLimit ? 300 : 200 }}>
                {assetCustomFields.map((assetCustomField: AssetCustomField, index) => (
                    <li key={index} className='asset-custom-fields_text'>
                        <div className='asset-custom-fields_text'>
                            <p className='asset-custom-fields_label'>{assetCustomField.name}</p>
                            {assetCustomField.type === "Date" ? (
                                <input
                                    type="date"
                                    className='asset-custom-fields_input'
                                    value={fieldValues?.[index] || ''}
                                    onChange={(e) => handleInputChange(index, e.target.value)}
                                />
                            ) : assetCustomField.type === "Numeric" ? (
                                <input
                                    type="number"
                                    className='asset-custom-fields_input'
                                    value={fieldValues?.[index] || ''}
                                    onChange={(e) => handleInputChange(index, e.target.value)}
                                />
                            ) : assetCustomField.type === "List" && assetCustomField.listValue && assetCustomField.listValue.length > 0 ? (
                                <select
                                    className='asset-custom-fields_select'
                                    value={fieldValues?.[index] || ''}
                                    onChange={(e) => handleInputChange(index, e.target.value)}
                                >
                                    <option value=""></option>
                                    {assetCustomField.listValue.map((option: string, i: number) => (
                                        <option key={i} value={option.trim()}>
                                            {option.trim()}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    className='asset-custom-fields_input'
                                    value={fieldValues?.[index] || ''}
                                    onChange={(e) => handleInputChange(index, e.target.value)}
                                />
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default AssetCustomFieldsComponent;
